/**
 * Navigation + access model for TugOS.
 *
 * `PAGE_ACCESS` is the single source of truth mapping each page slug to the
 * business roles allowed to see/reach it. It drives BOTH the sidebar nav
 * (visibleMenu) and the route guards (App.jsx imports it), and mirrors the
 * backend authorization map so the UI never offers a link the API would reject.
 *
 * 'any' = any authenticated user.
 */

/** Business role names — must match the seeded `roles` table exactly. */
export const ROLES = {
  ADMIN: 'System Administrator',
  DISPATCHER: 'Operations Monitor / Dispatcher',
  FINANCE: 'Billing / Finance Officer',
  TARIFF: 'Pricing / Tariff Manager',
  BOOKING: 'Sales & Marketing',
  CUSTOMER: 'Customer',
  AFFILIATE: 'Affiliate Operator',
  PILOT: 'Pilot',
  CAPTAIN: 'Tug Captain',
  ENGINEER: 'Chief Engineer',
  MAINT: 'Maintenance & Bunkering Coordinator',
};
const R = ROLES;

/** slug -> allowed business roles (or 'any'). */
export const PAGE_ACCESS = {
  dashboard: 'any',

  // Governance (System Administrator)
  'user-accounts-roles-permissions-rbac': [R.ADMIN],
  'harbor-geography': [R.ADMIN],
  // Administration / Configuration masters (8947 / 8951).
  'service-maintenance': [R.ADMIN],
  'assistance-areas': [R.ADMIN],
  'customer-approvals': [R.ADMIN],
  'external-service-integrations-settings': [R.ADMIN],
  'immutable-audit-trails-logger': [R.ADMIN],
  'saas-provider-billing-desk-invoicing': [R.ADMIN],

  // Operations & dispatch (Dispatcher, with shared pages)
  'live-tracking-map-operations-console': [R.DISPATCHER],
  'ops-wallboard': [R.DISPATCHER],
  'owned-tug-asset-database-maintenance-logs': [R.DISPATCHER],
  'vessel-master-registry-port-call-log': [R.DISPATCHER],
  'crew-roster-certifications-compliance': [R.DISPATCHER],
  'fleet-scheduler-captain-gantt-timeline': [R.DISPATCHER],
  'dispatch-planning-resource-allocation': [R.DISPATCHER],
  // Maintenance & bunkering windows — Coordinator manages; Dispatcher read-only.
  'maintenance-bunkering-scheduler': [R.MAINT, R.DISPATCHER],
  'fleet-performance-operations-ppa-reports': [R.DISPATCHER],
  'order-triaging-assisted-bookings': [R.DISPATCHER, R.BOOKING],
  'pilot-assignments-market-coordinator': [R.DISPATCHER, R.PILOT],
  'affiliate-broadcasts-job-review': [R.DISPATCHER, R.AFFILIATE],
  'affiliate-portal-capacity-register': [R.AFFILIATE, R.DISPATCHER],

  // Captain (mobile PWA)
  'captain-dispatch-ticket-execution-clock-pwa': [R.CAPTAIN],
  'daily-tug-report': [R.CAPTAIN, R.ENGINEER],
  'statement-of-facts-finalizer-delay-logger': [R.CAPTAIN],

  // Billing & commercial (Finance)
  // The Tariff Manager sets a special rate for a job here; Finance approves the bill.
  'invoicing-draft-billing-approver': [R.FINANCE, R.TARIFF],
  'sap-s4hana-integration-queue': [R.FINANCE],
  'commercial-revenues-costs-dashboard': [R.FINANCE],
  'affiliate-costs-intercompany-audit': [R.FINANCE],
  // Operations corrects clock times too (8927).
  'execution-clock-corrections': [R.FINANCE, R.DISPATCHER],

  // Pricing (Tariff Manager)
  'tariff-rate-cards-version-manager': [R.TARIFF],
  'allocation-rules-compulsory-floors': [R.TARIFF],

  // Customer self-service
  // Office roles see every customer's bookings here (8817); customers see their own.
  'customer-booking-form-live-dashboard': [R.CUSTOMER, R.BOOKING, R.ADMIN, R.DISPATCHER],
  'customer-favorite-vessels-preferences': [R.CUSTOMER],
  'vessel-master-arrival': [R.CUSTOMER],
};

/** Roles allowed for a slug (array), or null for unknown. */
export function rolesForSlug(slug) {
  return PAGE_ACCESS[slug] || null;
}

export const menuTree = [
  { label: 'Dashboard', pageId: 72883, slug: 'dashboard', icon: 'fa fa-tachometer-alt' },
  {
    label: 'Billing & SAP Integration',
    icon: 'fa fa-file-invoice',
    children: [
      { label: 'SAP Sync Queue', pageId: 72900, slug: 'sap-s4hana-integration-queue', icon: 'fa fa-network-wired' },
      { label: 'Invoicing Drafts', pageId: 72901, slug: 'invoicing-draft-billing-approver', icon: 'fa fa-receipt' },
      { label: 'Tariff Pricing', pageId: 72902, slug: 'tariff-rate-cards-version-manager', icon: 'fa fa-file-invoice-dollar' },
      { label: 'Financial Analytics', pageId: 72909, slug: 'commercial-revenues-costs-dashboard', icon: 'fa fa-chart-line' },
      { label: 'Allocation Rules', pageId: null, slug: 'allocation-rules-compulsory-floors', icon: 'fa fa-balance-scale' },
      { label: 'Clock Corrections', pageId: null, slug: 'execution-clock-corrections', icon: 'fa fa-clock-rotate-left' },
      { label: 'Intercompany Audit', pageId: 72899, slug: 'affiliate-costs-intercompany-audit', icon: 'fa fa-file-invoice' },
    ],
  },
  { label: 'Execution Map', pageId: 72896, slug: 'live-tracking-map-operations-console', icon: 'fa fa-map-marked-alt' },
  { label: 'Ops Wallboard', pageId: null, slug: 'ops-wallboard', icon: 'fa fa-tower-broadcast' },
  {
    label: 'Affiliates Hub',
    icon: 'fa fa-handshake',
    children: [
      { label: 'Job Review', pageId: 72897, slug: 'affiliate-broadcasts-job-review', icon: 'fa fa-hands' },
      { label: 'Capacity Portal', pageId: 72898, slug: 'affiliate-portal-capacity-register', icon: 'fa fa-ship' },
    ],
  },
  {
    label: 'Operations Desk',
    icon: 'fa fa-anchor',
    children: [
      { label: 'Crew Rostering', pageId: 72886, slug: 'crew-roster-certifications-compliance', icon: 'fa fa-users' },
      { label: 'Owned Tugs', pageId: 72887, slug: 'owned-tug-asset-database-maintenance-logs', icon: 'fa fa-tools' },
      { label: 'Maintenance & Bunkering', pageId: null, slug: 'maintenance-bunkering-scheduler', icon: 'fa fa-wrench' },
      { label: 'Vessel Masters', pageId: 72888, slug: 'vessel-master-registry-port-call-log', icon: 'fa fa-ship' },
      { label: 'Fleet Scheduler', pageId: 72889, slug: 'fleet-scheduler-captain-gantt-timeline', icon: 'fa fa-calendar-alt' },
    ],
  },
  {
    label: 'Order & Dispatch',
    icon: 'fa fa-route',
    children: [
      { label: 'Order Triaging', pageId: 72890, slug: 'order-triaging-assisted-bookings', icon: 'fa fa-headset' },
      { label: 'Pilot Coordinator', pageId: 72891, slug: 'pilot-assignments-market-coordinator', icon: 'fa fa-user-tie' },
      { label: 'Resource Planning', pageId: 72892, slug: 'dispatch-planning-resource-allocation', icon: 'fa fa-drafting-compass' },
    ],
  },
  {
    label: 'Field Operations',
    icon: 'fa fa-id-badge',
    children: [
      { label: 'Captain Console', pageId: null, slug: 'captain-dispatch-ticket-execution-clock-pwa', icon: 'fa fa-id-badge' },
      { label: 'SoF Finalizer', pageId: null, slug: 'statement-of-facts-finalizer-delay-logger', icon: 'fa fa-file-signature' },
      { label: 'Daily Tug Report', pageId: null, slug: 'daily-tug-report', icon: 'fa fa-gas-pump' },
    ],
  },
  {
    label: 'My Account',
    icon: 'fa fa-user',
    children: [
      { label: 'Book a Movement', pageId: null, slug: 'customer-booking-form-live-dashboard', icon: 'fa fa-anchor' },
      { label: 'Vessel Arrival', pageId: null, slug: 'vessel-master-arrival', icon: 'fa fa-ship' },
      { label: 'Favorite Vessels', pageId: null, slug: 'customer-favorite-vessels-preferences', icon: 'fa fa-star' },
    ],
  },
  {
    label: 'System Governance',
    icon: 'fa fa-cog',
    children: [
      { label: 'Harbor Geography', pageId: null, slug: 'harbor-geography', icon: 'fa fa-map-marked-alt' },
      { label: 'Assistance Areas', pageId: null, slug: 'assistance-areas', icon: 'fa fa-draw-polygon' },
      { label: 'Service Maintenance', pageId: null, slug: 'service-maintenance', icon: 'fa fa-concierge-bell' },
      { label: 'Customer Approvals', pageId: null, slug: 'customer-approvals', icon: 'fa fa-user-shield' },
      { label: 'Immutable Audit Ledger', pageId: 72903, slug: 'immutable-audit-trails-logger', icon: 'fa fa-fingerprint' },
      { label: 'API Integrations', pageId: 72905, slug: 'external-service-integrations-settings', icon: 'fa fa-server' },
      { label: 'User Accounts (RBAC)', pageId: 72906, slug: 'user-accounts-roles-permissions-rbac', icon: 'fa fa-users-cog' },
      { label: 'Fleet Utilization & Reports', pageId: 72907, slug: 'fleet-performance-operations-ppa-reports', icon: 'fa fa-print' },
      { label: 'SaaS Billing (Provider Only)', pageId: 72910, slug: 'saas-provider-billing-desk-invoicing', icon: 'fa fa-lock text-danger' },
    ],
  },
];

/** The default landing page when no route is given. */
export const defaultMenuItem = { label: 'Dashboard', pageId: 72883, slug: 'dashboard', icon: 'fa fa-tachometer-alt' };

/** Flat list of every navigable leaf (pages, not group headers). */
export const menuLeaves = menuTree.flatMap((item) =>
  item.children && item.children.length ? item.children : [item]
);

export const pageBySlug = Object.fromEntries(menuLeaves.map((leaf) => [leaf.slug, leaf]));
export const slugByPageId = Object.fromEntries(menuLeaves.map((leaf) => [leaf.pageId, leaf.slug]));

/** Can a user with `roles` reach this leaf? */
export function canAccessLeaf(leaf, { roles } = {}) {
  const userRoles = roles || [];
  if (!userRoles.length) return false;
  const allowed = PAGE_ACCESS[leaf.slug];
  if (allowed === 'any') return true;
  if (!allowed) return false;
  return userRoles.some((r) => allowed.includes(r));
}

/** The menu tree filtered to what the user's roles may access. */
export function visibleMenu(authCtx) {
  return menuTree
    .map((item) => {
      if (item.children && item.children.length) {
        const children = item.children.filter((c) => canAccessLeaf(c, authCtx));
        return children.length ? { ...item, children } : null;
      }
      return canAccessLeaf(item, authCtx) ? item : null;
    })
    .filter(Boolean);
}

export default menuTree;
